/**
 * useModulePermissions — Hook centralizado para gate UI por permisos.
 *
 * Consume el endpoint `/api/me/permissions` y expone helpers:
 *   const { can, isSuperAdmin, role, loading } = useModulePermissions();
 *   if (can("citas", "editar")) { ... }
 *   if (isSuperAdmin) { ... }
 *
 * Singleton lazy: el endpoint se llama UNA vez por sesión (cacheado).
 * Para forzar recarga: `refresh()`.
 *
 * Iter 153 — Sustituye los checks `user?.role === "..."` dispersos por una
 * fuente única de permisos en backend.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";

let _cache = null;
let _inflight = null;

function _loadPermissions(axiosAuth) {
  if (_cache) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = axiosAuth().get("/me/permissions")
    .then(r => { _cache = r.data; return r.data; })
    .catch(() => ({ role: "", is_super_admin: false, permissions: {}, can: {} }))
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Invalida la caché (uso típico: tras cambiar de rol o login/logout). */
export function clearPermissionsCache() {
  _cache = null;
  _inflight = null;
}

export default function useModulePermissions() {
  const { axiosAuth, token } = useAuth();
  const [perms, setPerms] = useState(_cache);
  const [loading, setLoading] = useState(!_cache);

  useEffect(() => {
    if (!token || !axiosAuth) {
      setPerms(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    _loadPermissions(axiosAuth).then(d => {
      if (cancelled) return;
      setPerms(d);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [axiosAuth, token]);

  const can = useCallback((module, action = "ver") => {
    if (!perms) return false;
    if (perms.is_super_admin) return true;
    const key = `${module}.${action}`;
    if (perms.can && perms.can[key]) return true;
    // fallback: si tiene "todo" en el módulo, permitir
    const actions = perms.permissions?.[module] || [];
    return actions.includes("todo");
  }, [perms]);

  const refresh = useCallback(() => {
    clearPermissionsCache();
    if (!axiosAuth) return Promise.resolve();
    setLoading(true);
    return _loadPermissions(axiosAuth).then(d => {
      setPerms(d);
      setLoading(false);
      return d;
    });
  }, [axiosAuth]);

  return {
    can,
    refresh,
    loading,
    role: perms?.role || "",
    isSuperAdmin: !!perms?.is_super_admin,
    permissions: perms?.permissions || {},
  };
}
